import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../services/axiosInstance';
import {
  clearAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  persistAccessToken,
  persistRefreshToken,
} from '../services/authTokenRefresh';
import { isAuthPublicPath } from '../utils/authPaths';

interface AuthState {
  isAuthenticated: boolean;
  isAdminPasswordVerified: boolean;
  user?: {
    id?: string | number;
    role?: string;
    name?: string;
    username?: string;
    is_superuser?: boolean;
    is_brand_manager?: boolean;
    is_main_admin?: boolean;
    monitor_all_pools?: boolean;
    monitoring_pool_ids?: number[];
  } | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType {
  authState: AuthState;
  login: (username: string, password: string) => Promise<boolean>;
  register: (data: { username: string; email: string; password: string }) => Promise<boolean>;
  resetPassword: (email: string) => Promise<boolean>;
  resetPasswordConfirm: (token: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyAdminPassword: (password: string) => Promise<boolean>;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isAdminPasswordVerified: false,
    user: null,
    loading: true,
    error: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const adminPasswordVerified = localStorage.getItem('adminPasswordVerified') === 'true';
      const hasSession =
        Boolean(getStoredAccessToken()) || Boolean(getStoredRefreshToken());

      if (!hasSession) {
        if (!cancelled) {
          setAuthState({
            isAuthenticated: false,
            isAdminPasswordVerified: adminPasswordVerified,
            user: null,
            loading: false,
            error: null,
          });
        }
        return;
      }

      try {
        const { data: user } = await axiosInstance.get('/api/auth/me');
        if (cancelled) return;
        setAuthState({
          isAuthenticated: true,
          isAdminPasswordVerified: adminPasswordVerified,
          user: user ?? null,
          loading: false,
          error: null,
        });
      } catch {
        clearAuthTokens();
        if (cancelled) return;
        setAuthState({
          isAuthenticated: false,
          isAdminPasswordVerified: adminPasswordVerified,
          user: null,
          loading: false,
          error: null,
        });
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      }));
      if (!isAuthPublicPath()) {
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, [navigate]);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      console.log(`[AuthContext] 로그인 시도: ${username}`);
      // localhost:3500 자동 로그인 우회 코드 제거
      // 항상 실제 로그인 API 호출
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });
      console.log(`[AuthContext] 응답 받음:`, response.data);
      if (response.data.access_token) {
        persistAccessToken(response.data.access_token);
        if (response.data.refresh_token) {
          persistRefreshToken(response.data.refresh_token);
        }
        try {
          const { data: me } = await axiosInstance.get('/api/auth/me');
          setAuthState((prev) => ({
            ...prev,
            isAuthenticated: true,
            isAdminPasswordVerified:
              prev.isAdminPasswordVerified ||
              localStorage.getItem('adminPasswordVerified') === 'true',
            user: me ?? response.data.user ?? null,
            loading: false,
            error: null,
          }));
          return true;
        } catch (verifyErr) {
          console.error('[AuthContext] 토큰 검증 실패 (/api/auth/me):', verifyErr);
          clearAuthTokens();
          setAuthState((prev) => ({
            ...prev,
            isAuthenticated: false,
            loading: false,
            error:
              '로그인 토큰이 API에서 거부됨. 백엔드를 재시작한 뒤 다시 로그인.',
          }));
          return false;
        }
      }
      return false;
    } catch (err) {
      console.error(`[AuthContext] 로그인 오류:`, err);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: '로그인에 실패'
      }));
      return false;
    }
  };

  const logout = () => {
    clearAuthTokens();
    localStorage.removeItem('adminPasswordVerified');
    setAuthState(prev => ({
      ...prev,
      isAuthenticated: false,
      isAdminPasswordVerified: false
    }));
    navigate('/login');
  };

  const register = async (data: { username: string; email: string; password: string }): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await axios.post('/api/auth/register', data);
      setAuthState(prev => ({ ...prev, loading: false }));
      return true;
    } catch (err: any) {
      setAuthState(prev => ({ ...prev, loading: false, error: err?.response?.data?.message || '회원가입에 실패' }));
      return false;
    }
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await axios.post('/api/auth/reset-password', { email });
      setAuthState(prev => ({ ...prev, loading: false }));
      return true;
    } catch (err: any) {
      setAuthState(prev => ({ ...prev, loading: false, error: err?.response?.data?.message || '비밀번호 재설정 요청에 실패' }));
      return false;
    }
  };

  const resetPasswordConfirm = async (token: string, password: string): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await axios.post('/api/auth/reset-password/confirm', { token, password });
      setAuthState(prev => ({ ...prev, loading: false }));
      return true;
    } catch (err: any) {
      setAuthState(prev => ({ ...prev, loading: false, error: err?.response?.data?.message || '비밀번호 변경에 실패' }));
      return false;
    }
  };

  const verifyAdminPassword = async (password: string): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const response = await axios.post('/api/auth/admin/password/verify', { password });
      console.log('관리자 비밀번호 검증 응답:', response.data);
      // 다양한 응답 구조에 대응
      if (response.data.verified || response.data.success) {
        localStorage.setItem('adminPasswordVerified', 'true');
        setAuthState(prev => ({
          ...prev,
          isAdminPasswordVerified: true,
          loading: false,
          error: null
        }));
        return true;
      }
      // 실패 응답
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: response.data.message || '잘못된 비밀번호'
      }));
      return false;
    } catch (err: any) {
      console.error('Admin password verification error:', err, err?.response?.data);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.message || '비밀번호 검증 중 오류 발생'
      }));
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        register,
        resetPassword,
        resetPasswordConfirm,
        logout,
        verifyAdminPassword,
        isAuthenticated: authState.isAuthenticated,
        loading: authState.loading,
        error: authState.error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 